/**
 * Key material, kept where the system will not throw it away (ADR-016).
 *
 * The ratchet's sessions and this device's identity lived in localStorage, and
 * inside a WebView `navigator.storage.persist()` is REFUSED on both platforms —
 * measured, not assumed:
 *
 *     [Storage] persistence REFUSED — keys may be evicted     (iOS and Android)
 *
 * Losing a session is not losing a setting. The far end keeps sending into a
 * ratchet whose other half is gone, and every message from then on reads as
 * damaged until they happen to start a new session — which they have no reason
 * to do, because nothing tells them.
 *
 * So on the native clients this lives in the Keychain and in
 * EncryptedSharedPreferences: it survives storage pressure, survives a WebView
 * data clear, and is not readable by another app. In a browser there is no such
 * place and localStorage remains, honestly and with a warning.
 *
 * Reads are synchronous from an in-memory copy, because Identity needs them to
 * be. The copy is filled once at startup: Android answers on the spot, iOS
 * cannot and pushes the answer back instead.
 */
const MEM = new Map();

let backend = 'localStorage';
let hydrated = null;

const native = () => (typeof window !== 'undefined' ? window.VoxioxNative : null);

/**
 * Fill the in-memory copy. Everything above this must await it once — reading
 * a key before it lands would look exactly like a device with no keys, and the
 * app would helpfully generate new ones.
 */
export function hydrateSecureStore({ timeoutMs = 5000 } = {}) {
  if (hydrated) return hydrated;
  hydrated = (async () => {
    const n = native();

    // Android: a synchronous answer across the bridge.
    if (n && typeof n.secureAll === 'function') {
      try {
        const all = JSON.parse(n.secureAll() || '{}');
        for (const [k, v] of Object.entries(all)) MEM.set(k, v);
        backend = 'android-encrypted-prefs';
        console.log('[SecureStore] keychain-backed,', MEM.size, 'keys');
        return backend;
      } catch (e) {
        console.warn('[SecureStore] Android secure storage unreadable:', e?.message);
      }
    }

    // iOS: ask, and wait for the push back.
    if (n && typeof n.requestSecureAll === 'function') {
      const got = await new Promise((resolve) => {
        window.VoxioxBridge = window.VoxioxBridge || {};
        window.VoxioxBridge.onSecureHydrate = (json) => {
          try {
            for (const [k, v] of Object.entries(JSON.parse(json || '{}'))) MEM.set(k, v);
            resolve(true);
          } catch (e) { resolve(false); }
        };
        try { n.requestSecureAll(); } catch (e) { resolve(false); }
        // A bridge that never answers must not hang the app for ever — but it
        // must also not be mistaken for "no keys", so the fallback is loud.
        setTimeout(() => resolve(false), timeoutMs);
      });
      if (got) {
        backend = 'ios-keychain';
        console.log('[SecureStore] keychain-backed,', MEM.size, 'keys');
        return backend;
      }
      console.warn('[SecureStore] native storage did not answer — falling back to localStorage');
    }

    // Browser: no such place exists.
    backend = 'localStorage';
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PREFIX)) MEM.set(k.slice(PREFIX.length), localStorage.getItem(k));
      }
    } catch (e) { /* private mode */ }
    console.log('[SecureStore] localStorage,', MEM.size, 'keys — may be evicted');
    return backend;
  })();
  return hydrated;
}

const PREFIX = 'voxiox_secure:';

export function secureBackend() { return backend; }
export function isEvictable() { return backend === 'localStorage'; }

export function secureGet(key) {
  return MEM.has(key) ? MEM.get(key) : null;
}

export function secureSet(key, value) {
  const v = String(value);
  MEM.set(key, v);
  const n = native();
  if (n && typeof n.secureSet === 'function') {
    try { n.secureSet(key, v); return; } catch (e) { /* fall through */ }
  }
  try { localStorage.setItem(PREFIX + key, v); } catch (e) { /* full or blocked */ }
}

export function secureRemove(key) {
  MEM.delete(key);
  const n = native();
  if (n && typeof n.secureRemove === 'function') {
    try { n.secureRemove(key); return; } catch (e) { /* fall through */ }
  }
  try { localStorage.removeItem(PREFIX + key); } catch (e) {}
}

/**
 * The NAMES of what is held — never the values. Whether a session for a given
 * device exists at all is the first question when messages stop opening, and
 * without this it can only be answered by guessing.
 */
export function secureKeyNames() { return [...MEM.keys()]; }

/** Synchronous face, for Identity, which needs one. */
export const secureSyncStorage = {
  getItem: (k) => secureGet(k),
  setItem: (k, v) => secureSet(k, v),
  removeItem: (k) => secureRemove(k),
};

/** Async face, for the ratchet's session store. */
export const secureAsyncStore = {
  get: async (k) => { const v = secureGet(k); return v ? JSON.parse(v) : null; },
  set: async (k, v) => secureSet(k, JSON.stringify(v)),
  del: async (k) => secureRemove(k),
};

/**
 * One-time move of anything already in localStorage. Without it every current
 * device becomes a new device to all its contacts on the next release, and the
 * warning that produces is supposed to mean something.
 */
export function adoptFromLocalStorage(keys) {
  for (const k of keys) {
    if (MEM.has(k)) continue;
    let legacy = null;
    try { legacy = localStorage.getItem(k); } catch (e) { continue; }
    if (legacy == null) continue;
    secureSet(k, legacy);
    try { localStorage.removeItem(k); } catch (e) {}
    console.log('[SecureStore] moved', k, 'out of localStorage');
  }
}
